import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useToast } from '../hooks/useToast';
import { getPricing, createPricing, updatePricing, deletePricing, recalculateCosts, getUnknownPricing } from '../services/costs';
import { setTitle } from '../utils/environment';
import PricingForm from './PricingForm';
import PageLoading from '../components/PageLoading';

const PricingManagement = () => {
  const { user, logout } = useAuth();
  const { showError, showSuccess } = useToast();
  const [searchParams] = useSearchParams();
  const [pricing, setPricing] = useState([]);
  const [unknownPricing, setUnknownPricing] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [recalculating, setRecalculating] = useState(false);

  // Form state
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    provider: '',
    model: '',
    input_cost_per_1m: '',
    output_cost_per_1m: '',
    per_request_cost: '',
    effective_from: new Date().toISOString().split('T')[0]
  });

  // Recalculate form state
  const [recalcStartDate, setRecalcStartDate] = useState('');
  const [recalcEndDate, setRecalcEndDate] = useState('');
  const [recalcResult, setRecalcResult] = useState(null);
  const [forbidden, setForbidden] = useState(false);

  /**
   * Load pricing data from API
   */
  const loadData = async () => {
    try {
      setLoading(true);
      const [pricingData, unknownData] = await Promise.all([
        getPricing(),
        getUnknownPricing()
      ]);
      setPricing(pricingData);
      setUnknownPricing(unknownData);

      // Check URL params for pre-fill
      const provider = searchParams.get('provider');
      const model = searchParams.get('model');
      if (provider && model) {
        setFormData(prev => ({
          ...prev,
          provider,
          model
        }));
        setShowForm(true);
      }
    } catch (error) {
      console.error('Failed to load pricing:', error);
      if (error.response?.status === 403) {
        setForbidden(true);
      } else {
        showError(error.response?.data?.detail || 'Failed to load pricing data');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setTitle('Pricing · Read It Never');
    loadData();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const resetForm = () => {
    setFormData({
      provider: '',
      model: '',
      input_cost_per_1m: '',
      output_cost_per_1m: '',
      per_request_cost: '',
      effective_from: new Date().toISOString().split('T')[0]
    });
    setEditingId(null);
    setShowForm(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      const data = {
        provider: formData.provider,
        model: formData.model,
        input_cost_per_1m: formData.input_cost_per_1m ? parseFloat(formData.input_cost_per_1m) : null,
        output_cost_per_1m: formData.output_cost_per_1m ? parseFloat(formData.output_cost_per_1m) : null,
        per_request_cost: formData.per_request_cost ? parseFloat(formData.per_request_cost) : null,
        effective_from: formData.effective_from
      };

      if (editingId) {
        await updatePricing(editingId, data);
        showSuccess('Pricing updated successfully');
      } else {
        await createPricing(data);
        showSuccess('Pricing created successfully');
      }
      resetForm();
      loadData();
    } catch (error) {
      console.error('Failed to save pricing:', error);
      showError(error.response?.data?.detail || 'Failed to save pricing');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (item) => {
    setFormData({
      provider: item.provider,
      model: item.model,
      input_cost_per_1m: item.input_cost_per_1m || '',
      output_cost_per_1m: item.output_cost_per_1m || '',
      per_request_cost: item.per_request_cost || '',
      effective_from: item.effective_from
    });
    setEditingId(item.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this pricing entry?')) {
      return;
    }
    try {
      await deletePricing(id);
      showSuccess('Pricing deleted successfully');
      loadData();
    } catch (error) {
      console.error('Failed to delete pricing:', error);
      showError(error.response?.data?.detail || 'Failed to delete pricing');
    }
  };

  const handleRecalculate = async (e) => {
    e.preventDefault();
    if (!recalcStartDate || !recalcEndDate) {
      showError('Please select both start and end dates');
      return;
    }
    try {
      setRecalculating(true);
      const result = await recalculateCosts(recalcStartDate, recalcEndDate);
      setRecalcResult(result);
      showSuccess(`Recalculated ${result.updated_count} logs`);
    } catch (error) {
      console.error('Failed to recalculate costs:', error);
      showError(error.response?.data?.detail || 'Failed to recalculate costs');
    } finally {
      setRecalculating(false);
    }
  };

  const handleAddFromUnknown = (item) => {
    setFormData({
      provider: item.provider,
      model: item.model,
      input_cost_per_1m: '',
      output_cost_per_1m: '',
      per_request_cost: '',
      effective_from: new Date().toISOString().split('T')[0]
    });
    setEditingId(null);
    setShowForm(true);
  };

  if (forbidden) {
    return (
      <div className="min-h-screen bg-clay-bg flex items-center justify-center">
        <div className="card-clay text-center p-8">
          <div className="text-6xl mb-4">🔒</div>
          <h2 className="font-display text-2xl font-bold text-heading mb-2">Admin Access Required</h2>
          <p className="text-secondary-text mb-4">You don&apos;t have permission to access pricing management.</p>
          <button
            onClick={() => window.history.back()}
            className="btn-clay-accent"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <PageLoading message="Loading pricing data..." />
    );
  }

  return (
    <div className="min-h-screen bg-clay-bg">
      {/* Header */}
      <header className="bg-surface shadow-clay border-b-3 border-clay-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center">
              <button
                onClick={() => window.history.back()}
                className="mr-4 p-2 text-secondary-text hover:text-primary-600 hover:bg-primary-50 rounded-clay transition-all duration-200"
              >
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <div className="h-10 w-10 mr-3">
                <img src={new URL('../assets/logo.png', import.meta.url).href} alt="Read It Never" className="h-10 w-10 rounded-clay shadow-clay-sm" />
              </div>
              <div>
                <h1 className="font-display text-xl font-bold text-heading">Pricing Management</h1>
                <p className="text-sm text-secondary-text">Configure LLM pricing for cost tracking</p>
              </div>
            </div>

            <div className="flex items-center space-x-4">
              <Link
                to="/admin/costs"
                className="btn-clay-secondary btn-clay-sm flex items-center"
              >
                <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
                Cost Dashboard
              </Link>
              <span className="badge-fun-primary text-xs">{user?.email?.split('@')[0]}</span>
              <button
                onClick={logout}
                className="btn-clay-ghost text-sm"
              >
                Sign out
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Unknown Pricing Alert */}
        {unknownPricing.length > 0 && (
          <section className="mb-8">
            <div className="card-clay-sm border-l-4 border-l-fun-peach bg-fun-peach/10 mb-4">
              <div className="flex items-start">
                <span className="text-2xl mr-3">⚠️</span>
                <div>
                  <h3 className="font-display font-semibold text-heading">Models Missing Pricing</h3>
                  <p className="text-sm text-secondary-text mt-1">
                    Add pricing for these models to enable cost tracking.
                  </p>
                </div>
              </div>
            </div>
            <div className="card-clay overflow-hidden">
              <table className="min-w-full divide-y divide-clay-border">
                <thead className="bg-clay-bg">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-secondary-text uppercase tracking-wider">Provider</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-secondary-text uppercase tracking-wider">Model</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-secondary-text uppercase tracking-wider">Requests</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-secondary-text uppercase tracking-wider">Action</th>
                  </tr>
                </thead>
                <tbody className="bg-surface divide-y divide-clay-border">
                  {unknownPricing.map((item, idx) => (
                    <tr key={idx} className="bg-fun-peach/10">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-heading capitalize">{item.provider}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-secondary-text font-mono">{item.model}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-heading text-right">{item.count.toLocaleString()}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <button
                          onClick={() => handleAddFromUnknown(item)}
                          className="text-primary-600 hover:text-primary-700 text-sm font-medium"
                        >
                          Add Pricing
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {/* Add/Edit Form + Pricing Table */}
        <section className="mb-8">
          <PricingForm
            showForm={showForm}
            setShowForm={setShowForm}
            editingId={editingId}
            formData={formData}
            onInputChange={handleInputChange}
            onSubmit={handleSubmit}
            onReset={resetForm}
            saving={saving}
          />

          {/* Pricing Table */}
          <div className="card-clay overflow-hidden">
            {pricing.length > 0 ? (
              <table className="min-w-full divide-y divide-clay-border">
                <thead className="bg-clay-bg">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-secondary-text uppercase tracking-wider">Provider</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-secondary-text uppercase tracking-wider">Model</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-secondary-text uppercase tracking-wider">Input/1M</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-secondary-text uppercase tracking-wider">Output/1M</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-secondary-text uppercase tracking-wider">Per Request</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-secondary-text uppercase tracking-wider">Effective From</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-secondary-text uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-surface divide-y divide-clay-border">
                  {pricing.map((item) => (
                    <tr key={item.id} className="hover:bg-clay-bg transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-heading capitalize">{item.provider}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-secondary-text font-mono">{item.model}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-heading text-right">
                        {item.input_cost_per_1m ? `$${item.input_cost_per_1m}` : '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-heading text-right">
                        {item.output_cost_per_1m ? `$${item.output_cost_per_1m}` : '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-heading text-right">
                        {item.per_request_cost ? `$${item.per_request_cost}` : '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-secondary-text text-right">{item.effective_from}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm space-x-3">
                        <button
                          onClick={() => handleEdit(item)}
                          className="text-primary-600 hover:text-primary-700 font-medium"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDelete(item.id)}
                          className="text-fun-coral hover:text-fun-coral/80 font-medium"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p className="text-secondary-text text-center italic py-8">No pricing configured. Add pricing to start tracking costs.</p>
            )}
          </div>
        </section>

        {/* Recalculate Section */}
        <section className="mb-8">
          <h2 className="font-display text-2xl font-bold text-heading mb-4">Recalculate Costs</h2>
          <div className="card-clay p-6">
            <p className="text-sm text-secondary-text mb-4">
              Recalculate costs for existing usage logs using current pricing. Useful after adding or updating pricing for a model.
            </p>
            <form onSubmit={handleRecalculate} className="flex flex-wrap items-end gap-4">
              <div>
                <label className="block text-sm font-medium text-body-text mb-1">Start Date</label>
                <input
                  type="date"
                  value={recalcStartDate}
                  onChange={(e) => setRecalcStartDate(e.target.value)}
                  required
                  className="px-3 py-2 border-2 border-clay-border rounded-clay bg-surface text-body-text focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-body-text mb-1">End Date</label>
                <input
                  type="date"
                  value={recalcEndDate}
                  onChange={(e) => setRecalcEndDate(e.target.value)}
                  required
                  className="px-3 py-2 border-2 border-clay-border rounded-clay bg-surface text-body-text focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              <button
                type="submit"
                disabled={recalculating}
                className="btn-clay-secondary disabled:opacity-50"
              >
                {recalculating ? 'Recalculating...' : 'Recalculate'}
              </button>
            </form>
            {recalcResult && (
              <div className="mt-4 p-4 bg-fun-mint/20 border-l-4 border-fun-mint rounded-r-clay">
                <p className="text-sm text-heading">
                  Processed {recalcResult.total_processed.toLocaleString()} logs.
                  Updated {recalcResult.updated_count.toLocaleString()} with new costs.
                  {recalcResult.still_unknown > 0 && (
                    <span className="text-fun-peach font-medium"> {recalcResult.still_unknown.toLocaleString()} still missing pricing.</span>
                  )}
                  {recalcResult.total_cost_change !== 0 && (
                    <span> Total cost change: ${recalcResult.total_cost_change.toFixed(2)}</span>
                  )}
                </p>
              </div>
            )}
          </div>
        </section>

        {/* Pricing Reference */}
        <section className="mb-8">
          <h2 className="font-display text-2xl font-bold text-heading mb-4">Pricing Reference</h2>
          <div className="card-clay p-6">
            <p className="text-sm text-secondary-text mb-4">Common model pricing (per 1M tokens, as of 2024):</p>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="border-b-2 border-clay-border">
                    <th className="py-2 text-left font-medium text-body-text">Provider</th>
                    <th className="py-2 text-left font-medium text-body-text">Model</th>
                    <th className="py-2 text-right font-medium text-body-text">Input</th>
                    <th className="py-2 text-right font-medium text-body-text">Output</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-clay-border">
                  <tr><td className="py-2">OpenAI</td><td className="font-mono">gpt-4o-mini</td><td className="text-right">$0.15</td><td className="text-right">$0.60</td></tr>
                  <tr><td className="py-2">OpenAI</td><td className="font-mono">text-embedding-3-small</td><td className="text-right">$0.02</td><td className="text-right">-</td></tr>
                  <tr><td className="py-2">Gemini</td><td className="font-mono">gemini-3-flash-preview</td><td className="text-right">$0.10</td><td className="text-right">$0.40</td></tr>
                  <tr><td className="py-2">Gemini</td><td className="font-mono">gemini-2.0-flash</td><td className="text-right">$0.075</td><td className="text-right">$0.30</td></tr>
                  <tr><td className="py-2">Anthropic</td><td className="font-mono">claude-3-haiku-20240307</td><td className="text-right">$0.25</td><td className="text-right">$1.25</td></tr>
                  <tr><td className="py-2">Vertex AI</td><td className="font-mono">multimodalembedding@001</td><td className="text-right" colSpan="2">~$0.0001/image</td></tr>
                </tbody>
              </table>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default PricingManagement;
